/**
 * ReplayTable - Displays all players' hands around the table during replay
 * Card sizes and layout adapt dynamically to the viewport.
 */

import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import Card from './Card';
import type { CardNumber } from '../types/game';
import type { GameHistory, ReplayStepState, CardPlay } from '../game/gameHistory';
import type { DeclaredPoignee } from '../game/gameActions';
import { PoigneeNames } from '../game/gameActions';
import { sortHand } from '../utils/cards';
import { ContractNames } from '../types/game';

type ScreenPosition = 'bottom' | 'top' | 'left' | 'right' | 'top-left' | 'top-right';

interface ReplayTableProps {
  history: GameHistory;
  stepState: ReplayStepState;
  showBids?: boolean;
  showChien?: boolean;
  showEcart?: boolean;
}

function bidReviewLabel(bid: number): string {
  switch (bid) {
    case 0: return 'Passe';
    case 1: return 'Petite';
    case 2: return 'Garde';
    case 3: return 'Garde sans';
    case 4: return 'Garde contre';
    default: return String(bid);
  }
}

// Card aspect ratio (width / height) - from default 60x109
const CARD_ASPECT = 60 / 109;

function getScreenPositions(playerCount: number, myPosition: number): Map<number, ScreenPosition> {
  const positions = new Map<number, ScreenPosition>();
  const layouts: Record<number, ScreenPosition[]> = {
    3: ['bottom', 'right', 'left'],
    4: ['bottom', 'right', 'top', 'left'],
    5: ['bottom', 'right', 'top-right', 'top-left', 'left'],
  };
  const layout = layouts[playerCount] || layouts[4];
  for (let i = 0; i < playerCount; i++) {
    const relativePos = (i - myPosition + playerCount) % playerCount;
    positions.set(i, layout[relativePos] || 'bottom');
  }
  return positions;
}

export default function ReplayTable({ history, stepState, showBids, showChien, showEcart }: ReplayTableProps) {
  const { hands, trickCards } = stepState;
  const tableRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(600);
  const [tableWidth, setTableWidth] = useState(800);

  const measure = useCallback(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.clientHeight);
      setTableWidth(tableRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (tableRef.current) ro.observe(tableRef.current);
    return () => { window.removeEventListener('resize', measure); ro.disconnect(); };
  }, [measure]);

  // Dynamic card dimensions: min(height/5, width/9 adjusted for aspect ratio)
  const { cardWidth, cardHeight } = useMemo(() => {
    const effH = Math.max(tableHeight, 200);
    const effW = Math.max(tableWidth, 300);
    const h1 = effH / 5;
    const w2 = effW / 9;
    const h2 = w2 / CARD_ASPECT;
    const h = Math.min(h1, h2);
    const w = h * CARD_ASPECT;
    return { cardWidth: Math.round(w), cardHeight: Math.round(h) };
  }, [tableHeight, tableWidth]);

  // Proportional border radius (~13% of card width)
  const borderRadius = useMemo(
    () => Math.max(2, Math.round(cardWidth * 0.133)),
    [cardWidth]
  );

  // Smart layout margins based on table aspect ratio
  const { sideVertMargin, horizontalAvailable } = useMemo(() => {
    const isLandscape = tableWidth >= tableHeight;
    
    // Side hands (E/W): vertical margin at top/bottom of table
    const sideVM = isLandscape
      ? Math.round(cardWidth / 3)       // Landscape: small margin (1/3 card width)
      : Math.round(cardHeight * 1.5);   // Portrait: large margin (1.5 card height)
    
    // Horizontal hands (N/S): total available width
    const hMarginPerSide = isLandscape
      ? Math.round(cardHeight * 1.0)    // Landscape: margin of 1x card height per side
      : Math.round(cardWidth * 0.5);    // Portrait: small margin (0.5 card width per side)
    const hAvail = Math.max(cardWidth * 3, tableWidth - 2 * hMarginPerSide);
    
    return { sideVertMargin: sideVM, horizontalAvailable: hAvail };
  }, [tableWidth, tableHeight, cardWidth, cardHeight]);

  const screenPositions = useMemo(
    () => getScreenPositions(history.playerCount, history.myPosition),
    [history.playerCount, history.myPosition]
  );

  const playedCardSet = useMemo(() => {
    const set = new Set<CardNumber>();
    for (const play of trickCards) set.add(play.card);
    return set;
  }, [trickCards]);

  // CSS custom properties for dynamic card sizing and border radius
  const tableStyle = useMemo(() => ({
    '--card-small-width': `${cardWidth}px`,
    '--card-small-height': `${cardHeight}px`,
    '--card-border-radius': `${borderRadius}px`,
  } as React.CSSProperties), [cardWidth, cardHeight, borderRadius]);

  return (
    <div className="replay-table" ref={tableRef} style={tableStyle}>
      {Array.from(hands.entries()).map(([position, hand]) => {
        const screenPos = screenPositions.get(position) || 'bottom';
        const player = history.players[position];
        const isTaker = position === history.taker;
        const sortedHand = sortHand(hand);
        const contractLabel = isTaker ? (ContractNames[history.contract] || 'Preneur') : '';
        return (
          <ReplayPlayerHand
            key={position}
            playerName={player?.name || `P${position}`}
            cards={sortedHand}
            screenPosition={screenPos}
            isTaker={isTaker}
            contractLabel={contractLabel}
            playedCards={playedCardSet}
            tableHeight={tableHeight}
            tableWidth={tableWidth}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            sideVertMargin={sideVertMargin}
            horizontalAvailable={horizontalAvailable}
          />
        );
      })}

      {showBids && history.bids.length > 0 && (
        <div className="replay-trick-center training-trick-reviewing training-phase-reviewing">
          <div className="training-trick-review-label">Enchères</div>
          <div className="training-bids-review">
            {history.bids.map((b, i) => {
              const pName = history.players[b.player]?.name || `P${b.player}`;
              return (
                <div key={i} className="training-bids-review-row">
                  <span className="training-bids-review-name">{pName}</span>
                  <span className={`training-bids-review-bid ${b.bid === 0 ? 'pass' : 'bid'}`}>
                    {bidReviewLabel(b.bid)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!showBids && trickCards.length > 0 && (
        <ReplayTrickCenter
          trickCards={trickCards}
          screenPositions={screenPositions}
        />
      )}

      {!showBids && showChien && stepState.chien.length > 0 && (
        <div className="replay-special-cards">
          <span className="replay-special-label">Chien</span>
          <div className="replay-special-cards-row">
            {stepState.chien.map((card) => (
              <Card key={card} card={card} size="small" />
            ))}
          </div>
        </div>
      )}

      {!showBids && showEcart && stepState.ecart.length > 0 && (
        <div className="replay-special-cards">
          <span className="replay-special-label">Ecart</span>
          <div className="replay-special-cards-row">
            {stepState.ecart.map((card) => (
              <Card key={card} card={card} size="small" />
            ))}
          </div>
        </div>
      )}

      {/* Poignées déclarées à ce sous-pas */}
      {stepState.poignees && stepState.poignees.length > 0 && (
        <ReplayPoigneeOverlay 
          poignees={stepState.poignees} 
          players={history.players} 
        />
      )}
    </div>
  );
}

// ============================================
// Poignée Overlay (Replay)
// ============================================

interface ReplayPoigneeOverlayProps {
  poignees: DeclaredPoignee[];
  players: { name: string; position: number }[];
}

function ReplayPoigneeOverlay({ poignees, players }: ReplayPoigneeOverlayProps) {
  return (
    <div className="replay-poignee-overlay">
      {poignees.map((poignee, idx) => {
        const playerName = players[poignee.player]?.name || `Joueur ${poignee.player}`;
        // Tri officiel : du plus haut atout au plus faible, excuse en dernier
        const sortedCards = [...poignee.cards].sort((a, b) => {
          if (a === 78) return 1;
          if (b === 78) return -1;
          return b - a;
        });
        return (
          <div key={`poignee-${idx}`} className="replay-poignee-item">
            <div className="replay-poignee-header">
              <span className="replay-poignee-player">{playerName}</span>
              <span className="replay-poignee-type">{PoigneeNames[poignee.type]}</span>
            </div>
            <div className="replay-poignee-cards">
              {sortedCards.map((card) => (
                <Card key={card} card={card} size="small" />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Center Trick
// ============================================

interface ReplayTrickCenterProps {
  trickCards: CardPlay[];
  screenPositions: Map<number, ScreenPosition>;
}

function ReplayTrickCenter({ trickCards, screenPositions }: ReplayTrickCenterProps) {
  return (
    <div className="replay-trick-center">
      {trickCards.map((play, idx) => {
        const pos = screenPositions.get(play.player) || 'bottom';
        return (
          <div
            key={`${play.card}-${idx}`}
            className={`replay-trick-slot replay-trick-slot-${pos}`}
            style={{ zIndex: idx + 1 }}
          >
            <Card card={play.card} size="small" />
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Player Hand
// ============================================

interface ReplayPlayerHandProps {
  playerName: string;
  cards: CardNumber[];
  screenPosition: ScreenPosition;
  isTaker: boolean;
  contractLabel: string;
  playedCards: Set<CardNumber>;
  tableHeight: number;
  tableWidth: number;
  cardWidth: number;
  cardHeight: number;
  sideVertMargin: number;
  horizontalAvailable: number;
}

function ReplayPlayerHand({
  playerName, cards, screenPosition, isTaker, contractLabel, playedCards,
  tableHeight, tableWidth, cardWidth, cardHeight,
  sideVertMargin, horizontalAvailable,
}: ReplayPlayerHandProps) {
  void tableWidth; // used via horizontalAvailable
  void cardHeight; // used indirectly via CSS variable and layout calculations in parent
  const isSide = screenPosition === 'left' || screenPosition === 'right';
  const isRight = screenPosition === 'right';

  // For rotated side cards, visual height = cardWidth
  const rotatedVisualHeight = cardWidth;

  // Side hands: wrapper height = min(rotatedVisualHeight, available/count)
  const sideWrapperHeight = useMemo(() => {
    if (!isSide || cards.length <= 0) return rotatedVisualHeight;
    const available = tableHeight - sideVertMargin * 2 - 20;
    if (available <= 0) return 20;
    const spacing = available / cards.length;
    return Math.max(12, Math.min(rotatedVisualHeight, spacing));
  }, [cards.length, isSide, tableHeight, rotatedVisualHeight, sideVertMargin]);

  /**
   * Horizontal hands: dynamic overlap.
   * Uses horizontalAvailable (already accounts for aspect-ratio-based margins).
   * Cap at 0 (touching, never gaps).
   */
  const horizontalOverlap = useMemo(() => {
    if (isSide || cards.length <= 1) return 0;
    const spacingPerCard = horizontalAvailable / cards.length;
    const overlap = spacingPerCard - cardWidth;
    return Math.max(-(cardWidth - 8), Math.min(0, overlap));
  }, [cards.length, isSide, horizontalAvailable, cardWidth]);

  // East (right): reverse card display order
  const displayCards = useMemo(() => {
    if (isRight) return [...cards].reverse();
    return cards;
  }, [cards, isRight]);

  // Side hand column: lock width to prevent any X shift with card count changes
  const cardsStyle: React.CSSProperties | undefined = isSide
    ? { width: `${cardWidth}px`, minWidth: `${cardWidth}px`, maxWidth: `${cardWidth}px` }
    : undefined;

  // Inline style for hand container: set maxWidth for horizontal hands
  const handStyle: React.CSSProperties | undefined = !isSide
    ? { maxWidth: `${horizontalAvailable}px` }
    : undefined;

  return (
    <div className={`replay-hand replay-hand-${screenPosition}`} style={handStyle}>
      <div className="replay-hand-cards" style={cardsStyle}>
        {displayCards.map((card, idx) => {
          const isPlayed = playedCards.has(card);
          const style: React.CSSProperties = {};
          
          if (isSide) {
            style.height = `${sideWrapperHeight}px`;
            // East: topmost card (idx=0) should be frontmost
            // West: bottommost card (highest idx) should be frontmost
            if (isRight) {
              style.zIndex = displayCards.length - idx;
            } else {
              style.zIndex = idx + 1;
            }
          } else if (idx > 0) {
            style.marginLeft = `${horizontalOverlap}px`;
          }
          
          return (
            <div
              key={`${card}-${idx}`}
              className={`replay-card-wrapper ${isPlayed ? 'replay-card-played' : ''}`}
              style={style}
            >
              <Card card={card} size="small" />
            </div>
          );
        })}
        {cards.length === 0 && (
          <div style={{ opacity: 0.3, color: '#fff', fontSize: 11 }}>(aucune carte)</div>
        )}
      </div>
      <span className={`replay-hand-label ${isTaker ? 'is-taker' : ''}`}>
        {playerName}{isTaker && contractLabel ? ` (${contractLabel.toLowerCase()})` : ''}
      </span>
    </div>
  );
}
